import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import { usePrompt } from 'hooks/usePrompt';

import { ENUMS, IMPORT_EXPORT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

const LargeColSpan = {
	md: 12
};

const CustomOrdersForm = NiceModal.create(({ formData }) => {
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					dateTimePattern: yup.string().nullable().required(),
					configList: yup.array().of(
						yup.object().shape({
							otoHeader: yup.string().nullable().required(),
							header: yup.string().nullable().required(),
							defaultValue: yup.string().nullable()
						})
					)
				})
				.required(),
		[]
	);

	const handleCloseModal = useCallback((status = false) => {
		modal.resolve({ status });
		modal.remove();
	}, []);

	const fillForm = useCallback(async () => {
		if (formData) {
			const resultConfig = [];

			if (formData?.headerJSON) {
				Object.keys(formData.headerJSON).forEach((key) => {
					if (key === 'dateTimePattern') return;
					resultConfig.push({
						otoHeader: key,
						header: formData.headerJSON[key] || null,
						defaultValue: formData?.defaultValuesJSON?.[key] || null
					});
				});
			}
			if (resultConfig.length === 0) {
				resultConfig.push({ otoHeader: '', header: '', defaultValue: null });
			}
			return {
				configList: resultConfig,
				dateTimePattern: formData?.headerJSON?.dateTimePattern || null
			};
		}
		return {
			configList: [{ otoHeader: '', header: '', defaultValue: null }],
			dateTimePattern: 'ddMMyyyy'
		};
	}, [formData]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		control,
		clearErrors,
		setError,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		try {
			const URL = formData ? IMPORT_EXPORT.UPDATE_CUSTOM_ORDER_TEMPLATE : IMPORT_EXPORT.ADD_CUSTOM_ORDER_TEMPLATE;

			const obj = {
				headerJSON: {},
				defaultValuesJSON: {}
			};

			const bodyObj = data.configList.reduce((acc, config) => {
				acc.headerJSON[config.otoHeader] = config.header || '';
				if (config.defaultValue) {
					acc.defaultValuesJSON[config.otoHeader] = config.defaultValue;
				}
				return acc;
			}, obj);

			bodyObj.headerJSON.dateTimePattern = data.dateTimePattern;
			const response = await axiosPrivate.post(URL, bodyObj);

			if (response.status === 200) {
				showSuccess();
				handleCloseModal(true);
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal fullwidth maxWidth="small" className="modal-custom-order">
			<ModalTitle>
				<Typography variant="p1">customOrderForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<Alert content="customOrderForm.description" className="m-4" showIcon />
					<FormItem colSpan={LargeColSpan} label="dateTimePattern" required>
						<EnumSelect
							name="dateTimePattern"
							enumName={ENUMS.CUSTOM_ORDER_DATE_TIME_PATTERN}
							setValue={(value) => {
								setValue('dateTimePattern', value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch('dateTimePattern')}
							searchable={false}
							valueRemovable={false}
						/>
					</FormItem>
					<FormItem>
						<ConfigurationTable
							isLoading={isLoading}
							control={control}
							clearErrors={clearErrors}
							watch={watch}
							schema={schema}
							setValue={setValue}
							setError={setError}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					loading={isLoading}
					label="save"
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					iconPos="right"
				/>
			</ModalActions>
		</Modal>
	);
});

const ConfigurationTable = ({ isLoading, control, watch, setValue }) => {
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const {
		fields: configListFields,
		append: appendConfigList,
		remove: removeConfigList
	} = useFieldArray({ control, name: 'configList' });

	const removeItem = async ({ index }) => {
		removeConfigList(index);
	};

	const columns = useMemo(
		() => [
			{
				id: 'configList.otoHeader',
				header: 'otoHeader',
				accessor: 'configList.otoHeader',
				size: isMobile ? 40 : 100,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						enumName={ENUMS.CUSTOM_ORDER_HEADERS}
						valueRemovable={false}
						name={`configList[${index}].otoHeader`}
						setValue={(value) => {
							setValue(`configList[${index}].otoHeader`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						value={watch(`configList[${index}].otoHeader`)}
					/>
				),
				showOnMobile: true,
				required: true
			},
			{
				id: 'configList.header',
				header: 'header',
				accessor: 'configList.header',
				size: isMobile ? 40 : 100,
				renderCell: ({ row: { index } }) => <StringInput name={`configList[${index}].header`} required />,
				showOnMobile: true,
				required: true
			},
			{
				id: 'configList.defaultValue',
				header: 'defaultValue',
				accessor: 'configList.defaultValue',
				size: isMobile ? 40 : 100,
				renderCell: ({ row: { index } }) => <StringInput name={`configList[${index}].defaultValue`} />,
				showOnMobile: true
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20,
				showOnMobile: true
			}
		],
		[isMobile]
	);

	return (
		<Table
			loading={isLoading}
			data={configListFields}
			columns={columns}
			onRowDelete={(row) => {
				removeItem(row);
			}}
			onRowAdd={() => {
				appendConfigList({});
			}}
			showAddNewRowButton
			initEditMode={(row) => row.id === null}
		/>
	);
};

export default CustomOrdersForm;
